import React, { useMemo, useState } from 'react';
import { useMood, MoodCheckInRecord } from '../../contexts/MoodContext';
import { getMood, MoodId } from './moods';
import {
  addMonths, dayKey, fmtLongDay, fmtMonthYear, monthGrid, monthKey, startOfMonth,
} from './moodDates';
import { MoodDonut, MoodBars, summarySentence } from './MoodCharts';
import CheckInList from './CheckInList';
import ReflectionPanel from './ReflectionPanel';

const topMoodOf = (records: MoodCheckInRecord[]): MoodId | undefined => {
  if (!records.length) return undefined;
  const counts = new Map<MoodId, number>();
  records.forEach((r) => counts.set(r.mood, (counts.get(r.mood) || 0) + 1));
  return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])[0][0];
};

/**
 * MonthlyView — a calendar of recorded moods plus month-level insights. Days are
 * clearly one of: no data (plain), one check-in (a dot), or several (a ringed
 * dot). A single recorded mood is never taken to stand for the whole day, and
 * missing days are never read as a mood.
 */
export const MonthlyView: React.FC = () => {
  const { checkIns } = useMood();
  const [anchor, setAnchor] = useState(new Date());
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const byDay = useMemo(() => {
    const map = new Map<string, MoodCheckInRecord[]>();
    checkIns.forEach((c) => {
      const k = dayKey(new Date(c.createdAt));
      const arr = map.get(k);
      if (arr) arr.push(c);
      else map.set(k, [c]);
    });
    return map;
  }, [checkIns]);

  const grid = useMemo(() => monthGrid(anchor), [anchor]);

  const inMonth = (c: MoodCheckInRecord) => {
    const d = new Date(c.createdAt);
    return d.getFullYear() === anchor.getFullYear() && d.getMonth() === anchor.getMonth();
  };
  const monthRecords = useMemo(() => checkIns.filter(inMonth), [checkIns, anchor]); // eslint-disable-line react-hooks/exhaustive-deps

  const prevAnchor = addMonths(anchor, -1);
  const prevRecords = useMemo(
    () => checkIns.filter((c) => {
      const d = new Date(c.createdAt);
      return d.getFullYear() === prevAnchor.getFullYear() && d.getMonth() === prevAnchor.getMonth();
    }),
    [checkIns, prevAnchor]
  );

  const daysWithData = useMemo(() => {
    const set = new Set<string>();
    monthRecords.forEach((c) => set.add(dayKey(new Date(c.createdAt))));
    return set.size;
  }, [monthRecords]);

  const canGoNext = startOfMonth(anchor).getTime() < startOfMonth(new Date()).getTime();
  const selectedRecords = selectedKey ? byDay.get(selectedKey) ?? [] : [];
  const selectedDate = selectedKey ? new Date(`${selectedKey}T00:00:00`) : null;

  const delta = monthRecords.length - prevRecords.length;
  const showCompare = monthRecords.length > 0 && prevRecords.length > 0;

  const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div className="mm-view">
      <div className="mm-navbar">
        <button type="button" className="mm-nav-arrow" aria-label="Previous month" onClick={() => { setAnchor((a) => addMonths(a, -1)); setSelectedKey(null); }}>‹</button>
        <div className="mm-navbar__label">
          <strong>{fmtMonthYear(anchor)}</strong>
          <button type="button" className="mm-link-btn" onClick={() => { setAnchor(new Date()); setSelectedKey(null); }}>This month</button>
        </div>
        <button type="button" className="mm-nav-arrow" aria-label="Next month" disabled={!canGoNext} onClick={() => { setAnchor((a) => addMonths(a, 1)); setSelectedKey(null); }}>›</button>
      </div>

      {/* Stat strip */}
      <div className="mm-stats">
        <div className="mm-stat"><span className="mm-stat__num">{monthRecords.length}</span><span className="mm-stat__lbl">check-ins</span></div>
        <div className="mm-stat"><span className="mm-stat__num">{daysWithData}</span><span className="mm-stat__lbl">days recorded</span></div>
        {showCompare && (
          <div className="mm-stat">
            <span className="mm-stat__num">{delta >= 0 ? `+${delta}` : delta}</span>
            <span className="mm-stat__lbl">vs last month</span>
          </div>
        )}
      </div>

      {/* Calendar */}
      <section className="mm-panel">
        <div className="mm-cal__weekdays" aria-hidden="true">
          {WEEKDAY_LABELS.map((w) => <span key={w}>{w}</span>)}
        </div>
        <div className="mm-cal">
          {grid.map((cell) => {
            const recs = byDay.get(cell.key) ?? [];
            const top = getMood(topMoodOf(recs));
            const many = recs.length > 1;
            return (
              <button
                key={cell.key}
                type="button"
                className={[
                  'mm-cal__cell',
                  cell.inMonth ? '' : 'is-outside',
                  cell.isToday ? 'is-today' : '',
                  recs.length ? 'has-data' : '',
                  many ? 'is-many' : '',
                  cell.key === selectedKey ? 'is-selected' : '',
                ].join(' ')}
                style={top ? { ['--mood-accent' as string]: top.accent } : undefined}
                onClick={() => cell.inMonth && setSelectedKey(cell.key)}
                disabled={!cell.inMonth}
                aria-label={`${fmtLongDay(cell.date)}, ${recs.length} check-in${recs.length === 1 ? '' : 's'}`}
              >
                <span className="mm-cal__num">{cell.date.getDate()}</span>
                {recs.length > 0 && <span className="mm-cal__dot" aria-hidden="true" />}
                {many && <span className="mm-cal__many" aria-hidden="true">{recs.length}</span>}
              </button>
            );
          })}
        </div>
        <div className="mm-cal__legend" aria-hidden="true">
          <span><span className="mm-cal__dot mm-cal__dot--legend" /> recorded</span>
          <span><span className="mm-cal__dot mm-cal__dot--legend is-many-legend" /> multiple</span>
          <span><span className="mm-cal__cell-mini" /> no data</span>
        </div>
      </section>

      {/* Selected day */}
      {selectedDate && (
        <section className="mm-panel">
          <h2 className="mm-panel__title">{fmtLongDay(selectedDate)}</h2>
          {selectedRecords.length ? <CheckInList records={selectedRecords} /> : <p className="mm-empty-sub">No check-ins recorded on this day.</p>}
        </section>
      )}

      {/* Distribution */}
      <div className="mm-two">
        <section className="mm-panel">
          <h2 className="mm-panel__title">Mood distribution</h2>
          <p className="mm-panel__sub">{summarySentence(monthRecords, 'this month')}</p>
          <MoodDonut records={monthRecords} />
        </section>
        <section className="mm-panel">
          <h2 className="mm-panel__title">By mood</h2>
          <MoodBars records={monthRecords} />
        </section>
      </div>

      {/* Reflection */}
      <section className="mm-panel">
        <h2 className="mm-panel__title">Monthly reflection</h2>
        <p className="mm-panel__sub">Optional and private — separate from the summary above.</p>
        <ReflectionPanel period="month" periodKey={monthKey(anchor)} />
      </section>
    </div>
  );
};

export default MonthlyView;
